import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { JourneyTimeline } from './JourneyTimeline';
import { Heart, Film, BarChart3, Monitor, Shield } from 'lucide-react';
const container = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const item = {
  hidden: {
    opacity: 0,
    x: -20
  },
  show: {
    opacity: 1,
    x: 0
  }
};
function ConsentPhoneMockup({
  src,
  alt,
  step,
  label,
  labelColor
}: {
  src: string;
  alt: string;
  step: string;
  label: string;
  labelColor: string;
}) {
  const [error, setError] = useState(false);
  return <div className="flex flex-col items-center gap-1.5" data-id="element-570">
      <span className="text-[11px] font-bold rounded-full px-2.5 py-0.5 whitespace-nowrap" style={{
      color: labelColor,
      backgroundColor: `${labelColor}15`
    }} data-id="element-571">
        {step} {label}
      </span>
      <div className="bg-[#1a1a1a] rounded-[22px] p-[4px] shadow-2xl w-[170px] shrink-0" data-id="element-572">
        <div className="bg-white rounded-[18px] overflow-hidden" data-id="element-573">
          {error ? <div className="flex items-center justify-center h-[260px] text-gray-300" data-id="element-574">
              <Monitor className="w-6 h-6" data-id="element-575" />
            </div> : <img src={src} alt={alt} className="w-full h-auto object-contain" onError={() => setError(true)} data-id="element-576" />}
        </div>
      </div>
    </div>;
}
export function SlidePostCongress() {
  const [imgError, setImgError] = useState(false);
  return <div className="h-full w-full bg-[#FDF8F8] text-[#2D2926] p-8 md:p-12 flex flex-col" data-id="element-577">
      <JourneyTimeline currentStage={5} data-id="element-578" />

      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} className="mb-5 border-l-4 border-[#B30010] pl-6" data-id="element-579">
        <div className="flex items-center gap-3 mb-2" data-id="element-580">
          <span className="text-4xl" data-id="element-581">🔁</span>
          <h2 className="text-3xl font-bold text-[#2D2926]" data-id="element-582">Post Congress</h2>
        </div>
        <p className="text-xl text-[#B30010] font-medium" data-id="element-583">
          Goal: Keep momentum & move prospects further down funnel
        </p>
      </motion.div>

      <div className="flex-1 grid grid-cols-1 md:grid-cols-[0.75fr_1.25fr] gap-6 min-h-0" data-id="element-584">
        {/* Left Column — Tactics & Features */}
        <div className="flex flex-col gap-4 overflow-auto" data-id="element-585">
          <motion.div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100" variants={container} initial="hidden" animate="show" data-id="element-586">
            <h3 className="text-base font-bold text-[#2D2926] mb-3 flex items-center gap-2" data-id="element-587">
              <span className="w-8 h-1 bg-[#B30010] rounded-full" data-id="element-588"></span>
              Tactics
            </h3>
            <ul className="space-y-2.5" data-id="element-589">
              <motion.li variants={item} className="flex items-start gap-3" data-id="element-590">
                <Heart className="w-4 h-4 text-[#B30010] mt-0.5 shrink-0" data-id="element-591" />
                <span className="text-gray-700 text-sm" data-id="element-592">
                  "Thank you" Snak™ with recap & highlights
                </span>
              </motion.li>
              <motion.li variants={item} className="flex items-start gap-3" data-id="element-593">
                <Film className="w-4 h-4 text-[#B30010] mt-0.5 shrink-0" data-id="element-594" />
                <span className="text-gray-700 text-sm" data-id="element-595">
                  Personalized "what you missed" reels
                </span>
              </motion.li>
              <motion.li variants={item} className="flex items-start gap-3" data-id="element-596">
                <BarChart3 className="w-4 h-4 text-[#B30010] mt-0.5 shrink-0" data-id="element-597" />
                <span className="text-gray-700 text-sm" data-id="element-598">
                  Polls or surveys to learn what resonated
                </span>
              </motion.li>
            </ul>
          </motion.div>

          <motion.div className="bg-[#B30010]/5 p-4 rounded-xl border border-[#B30010]/10" variants={container} initial="hidden" animate="show" data-id="element-599">
            <h3 className="text-base font-bold text-[#2D2926] mb-3 flex items-center gap-2" data-id="element-600">
              <span className="w-8 h-1 bg-[#FF6B6B] rounded-full" data-id="element-601"></span>
              Snakker™ Features
            </h3>
            <ul className="space-y-2.5" data-id="element-602">
              <motion.li variants={item} className="flex items-start gap-3" data-id="element-603">
                <div className="w-2 h-2 rounded-full bg-[#FF6B6B] mt-1.5 shrink-0" data-id="element-604" />
                <span className="text-gray-700 text-sm" data-id="element-605">
                  <strong className="text-[#2D2926]" data-id="element-606">Rating Cue™</strong> for
                  session feedback
                </span>
              </motion.li>
              <motion.li variants={item} className="flex items-start gap-3" data-id="element-607">
                <div className="w-2 h-2 rounded-full bg-[#FF6B6B] mt-1.5 shrink-0" data-id="element-608" />
                <span className="text-gray-700 text-sm" data-id="element-609">
                  <strong className="text-[#2D2926]" data-id="element-610">Goto Cue™</strong> to
                  navigate between topics
                </span>
              </motion.li>
              <motion.li variants={item} className="flex items-start gap-3" data-id="element-611">
                <div className="w-2 h-2 rounded-full bg-[#FF6B6B] mt-1.5 shrink-0" data-id="element-612" />
                <span className="text-gray-700 text-sm" data-id="element-613">
                  Output reports to support{' '}
                  <strong className="text-[#2D2926]" data-id="element-614">CRM follow-up</strong>
                </span>
              </motion.li>
            </ul>
          </motion.div>
        </div>

        {/* Right Column — AstraZeneca Consent Use Case */}
        <motion.div className="flex flex-col items-center justify-center min-h-0" initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.5,
        delay: 0.3
      }} data-id="element-615">
          {/* Header badge */}
          <motion.div className="flex items-center gap-2 mb-4 bg-[#2D2926] rounded-full px-4 py-1.5 shadow-sm" initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} data-id="element-616">
            <Shield className="w-3.5 h-3.5 text-[#FF6B6B]" data-id="element-617" />
            <span className="text-xs font-bold text-white" data-id="element-618">
              AstraZeneca — Consent Use Case
            </span>
          </motion.div>

          {/* Two-screen image */}
          <motion.div className="w-full max-w-lg rounded-xl overflow-hidden shadow-lg border border-gray-200 bg-white" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} data-id="element-619">
            <img src="https://cdn.magicpatterns.com/uploads/eZ1rNrZSe713sYQYmUmHuE/Screenshot_2026-03-24_at_09.53.10.png" alt="AstraZeneca consent flow — Get the report & leave details with consent" className="w-full h-auto object-contain" data-id="element-620" />
          </motion.div>

          <motion.div className="flex items-center gap-6 mt-3" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.6
        }} data-id="element-621">
            <div className="flex items-center gap-1.5" data-id="element-622">
              <span className="text-[11px] font-bold text-[#2D2926] bg-gray-100 rounded-full px-2.5 py-0.5" data-id="element-623">
                ①
              </span>
              <span className="text-[11px] text-gray-600" data-id="element-624">Get the Report</span>
            </div>
            <span className="text-gray-300" data-id="element-625">→</span>
            <div className="flex items-center gap-1.5" data-id="element-626">
              <span className="text-[11px] font-bold text-[#5B2D8E] bg-purple-50 rounded-full px-2.5 py-0.5" data-id="element-627">
                ②
              </span>
              <span className="text-[11px] text-gray-600" data-id="element-628">
                Details & Consent
              </span>
            </div>
          </motion.div>

          <motion.p className="text-[11px] text-gray-500 italic text-center mt-2" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.7
        }} data-id="element-629">
            AstraZeneca CKD campaign — HCPs explore the report, then opt in with
            full consent
          </motion.p>
        </motion.div>
      </div>
    </div>;
}